import { FontAwesome, Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { router, useFocusEffect, useNavigation } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { Platform, StyleSheet, TouchableOpacity, useColorScheme, View } from 'react-native';
import { Divider } from 'react-native-paper';

import { useGetDonationImpact } from '@/api/hooks/donationHooks';
import BellButton from '@/components/buttons/BellButton';
import NotificationButton from '@/components/buttons/NotificationButton';
import GuardWrapper from '@/components/GuardWrapper';
import { ImpactCard, ImpactCardColumn } from '@/components/impact/ImpactCard';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { Colors } from '@/constants/Colors';
import { useAuth } from '@/contexts/authContext';
import { formatCurrency } from '@/utils/currencyFormat';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { useGetMe } from '@/api/hooks/meHooks';

const IndividualProfile = () => {
  const colorScheme = useColorScheme();
  const { state, setCurrentUser } = useAuth();
  const navigation = useNavigation();

  const { data: impactData, refetch } = useGetDonationImpact({ userId: state.user?.user?.id ?? '' });
  const { data: meResponse, refetch: refetchGetMe } = useGetMe({ firebaseId: state.user?.user.firebaseId ?? '' });

  //Locales
  const screen_title = i18n.t('bottom_tab.profile');
  const total_donated = i18n.t('profile.impact.total_donated');
  const charities = i18n.t('profile.impact.charities');
  const churches = i18n.t('profile.impact.churches');
  const edit = i18n.t('mix.edit_title');

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={screen_title} />,
      headerRight: () => (
        <View style={[style.headerIcons, Platform.OS === 'web' && { marginRight: 16 }]}>
          <TouchableOpacity
            onPress={() => {
              router.navigate('/profile/settings');
            }}
            style={Platform.OS === 'web' && { marginRight: 16 }}>
            <Ionicons size={24} name="settings-sharp" style={style.roundedIcon} />
          </TouchableOpacity>
        </View>
      ),
      headerLeft: () => (
        <View style={[style.headerIcons, Platform.OS === 'web' && { marginLeft: 16 }]}>
          <BellButton hitSlop={10} style={style.roundedIcon} size={24} variant="alternative" iconColor="black" />
          <NotificationButton
            hitSlop={10}
            style={style.roundedIcon}
            size={24}
            iconColor="black"
            variant="alternative"
          />
        </View>
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (state.user?.user?.id) {
        refetch();
        refetchGetMe();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  useEffect(() => {
    if (meResponse?.user) setCurrentUser(meResponse?.user);
  }, [meResponse]);

  return (
    <GuardWrapper canAccess={['INDIVIDUAL']}>
      <View style={[style.container, { backgroundColor: Colors[colorScheme === 'dark' ? 'dark' : 'light'].background }]}>
        <View style={style.userPresentation}>
          {state.user?.user?.thumbnail ? (
            <Image source={firebaseImage(state.user?.user.thumbnail, ThumbSize.Small)} style={style.profilePic} />
          ) : (
            <FontAwesome name="user-circle-o" size={112} color="black" />
          )}
          <Text style={style.text} fontWeight="900">
            {state.user?.user?.fullName ?? 'Browse Only'}
          </Text>
          {state.user?.user?.biography && (
            <Text style={[style.description, { color: Colors[colorScheme === 'dark' ? 'dark' : 'light'].text }]}>
              {state.user?.user.biography}
            </Text>
          )}
          <TouchableOpacity
            onPress={() => {
              router.navigate('/profile/individual/edit');
            }}
            style={style.editBtn}>
            <Text style={style.editBtnLabel} fontWeight="700">
              {edit}
            </Text>
          </TouchableOpacity>
        </View>
        <Divider style={style.divider} />
        {impactData && (
          <View style={style.impactSection}>
            <View style={style.dontaedSection}>
              <Text style={style.donatedTitle}>{total_donated}</Text>
              <Text style={style.donatedNumber} fontWeight="700">
                {formatCurrency(impactData.groupByType.globalTotal)}
              </Text>
            </View>
            <ImpactCard>
              <ImpactCardColumn
                qty={impactData?.groupByType.charities.n}
                title={charities}
                amount={impactData?.groupByType.charities.totalAmount}
                onPress={() =>
                  router.navigate({
                    pathname: '/(app)/(bottomTabs)/profile/individual/impact/period/monthImpact',
                    params: {
                      npoType: 'charity'
                    }
                  })
                }
              />
              <ImpactCardColumn
                qty={impactData?.groupByType.church.n}
                title={churches}
                amount={impactData?.groupByType.church.totalAmount}
                onPress={() =>
                  router.navigate({
                    pathname: '/(app)/(bottomTabs)/profile/individual/impact/period/monthImpact',
                    params: {
                      npoType: 'church'
                    }
                  })
                }
              />
            </ImpactCard>
          </View>
        )}
      </View>
    </GuardWrapper>
  );
};

export default IndividualProfile;

const style = StyleSheet.create({
  profilePic: {
    width: 112,
    height: 112,
    borderRadius: 56
  },

  dontaedSection: {
    alignItems: 'center'
  },

  donatedTitle: {
    fontSize: 22,
    color: 'black'
  },
  donatedNumber: {
    fontSize: 28,
    color: 'black'
  },

  userPresentation: {
    marginVertical: 25,
    flexDirection: 'column',
    rowGap: 12,
    alignItems: 'center',
    width: '100%'
  },

  impactSection: {
    marginTop: 25,
    rowGap: 25
  },

  roundedIcon: {
    color: 'black',
    backgroundColor: 'white',
    borderRadius: (18 + 16) / 2,
    padding: 8,
    overflow: 'visible'
  },

  container: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 18
  },

  text: {
    color: 'black',
    fontSize: 18
  },

  description: {
    fontSize: 14,
    width: '100%',
    textAlign: 'center'
  },

  divider: {
    width: '100%'
  },

  editBtn: {
    paddingHorizontal: 35,
    paddingVertical: 8,
    backgroundColor: 'black',
    borderRadius: 20
  },

  editBtnLabel: {
    color: 'white',
    fontSize: 14
  },

  headerIcons: {
    flexDirection: 'row',
    gap: 10
  }
});
